import { PricingSection } from "@/pages/Home";
import { Check, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { COMPARISON_ROWS, FAQS } from "@/lib/mockData";

function renderComparisonCell(value) {
    if (typeof value !== "boolean") {
        return (
            <span className="font-mono font-medium text-slate-900 dark:text-slate-50">
                {value}
            </span>
        );
    }
    return value ? (
        <Check className="h-5 w-5 text-emerald-500 inline-block" />
    ) : (
        <X className="h-5 w-5 text-slate-300 inline-block" />
    );
}

export default function Pricing() {
    return (
        <div>
            <section className="pt-20 pb-12 relative">
                <div className="absolute inset-0 mesh-bg opacity-70" />
                <div className="container-tight relative text-center max-w-3xl">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
                        Pricing
                    </p>
                    <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-slate-50 text-balance">
                        Plans that grow with your business
                    </h1>
                    <p className="mt-5 text-lg text-slate-600 dark:text-slate-400">
                        Pick a plan, get clarity on every tender, and bid with confidence.
                        Switch or cancel anytime — no questions asked.
                    </p>
                </div>
            </section>

            <PricingSection />

            {/* COMPARISON TABLE */}
            <section className="py-24">
                <div className="container-tight">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
                            Compare Plans
                        </p>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                            All the details, side-by-side
                        </h2>
                    </div>
                    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                        <div className="grid grid-cols-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                            <div className="p-5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Feature
                            </div>
                            <div className="p-5 text-center">
                                <p className="font-semibold text-slate-900 dark:text-slate-50">
                                    Starter
                                </p>
                                <p className="text-xs text-slate-500 mt-1 font-mono">
                                    ₹299/mo
                                </p>
                            </div>
                            <div className="p-5 text-center bg-indigo-50 dark:bg-indigo-950/30">
                                <p className="font-semibold text-indigo-700 dark:text-indigo-300">
                                    Pro
                                </p>
                                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 font-mono">
                                    ₹499/mo
                                </p>
                            </div>
                            <div className="p-5 text-center">
                                <p className="font-semibold text-slate-900 dark:text-slate-50">
                                    Business
                                </p>
                                <p className="text-xs text-slate-500 mt-1 font-mono">
                                    ₹999/mo
                                </p>
                            </div>
                        </div>
                        {COMPARISON_ROWS.map((row, i) => (
                            <div
                                key={row.label}
                                className={`grid grid-cols-4 border-b border-slate-100 dark:border-slate-800/60 last:border-0 ${
                                    i % 2 === 1 ? "bg-slate-50/50 dark:bg-slate-950/30" : ""
                                }`}
                            >
                                <div className="p-4 text-sm font-medium text-slate-700 dark:text-slate-300">
                                    {row.label}
                                </div>
                                {["starter", "pro", "business"].map((k) => (
                                    <div
                                        key={k}
                                        className={`p-4 text-center text-sm ${
                                            k === "pro"
                                                ? "bg-indigo-50/40 dark:bg-indigo-950/20"
                                                : ""
                                        }`}
                                    >
                                        {renderComparisonCell(row[k])}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 bg-slate-50/50 dark:bg-slate-950/50 border-y border-slate-200 dark:border-slate-800">
                <div className="container-tight max-w-3xl">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                            Pricing questions
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-3">
                        {FAQS.slice(0, 5).map((f, i) => (
                            <AccordionItem
                                key={f.q}
                                value={`p-${i}`}
                                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6"
                            >
                                <AccordionTrigger className="text-left text-base font-semibold py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 dark:text-slate-400 leading-relaxed pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>

                    <div className="mt-12 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 text-center">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                            Need a custom plan?
                        </h3>
                        <p className="mt-2 text-slate-600 dark:text-slate-400">
                            For agencies and consultants analyzing 100+ tenders/month.
                        </p>
                        <Link to="/contact" className="mt-5 inline-block">
                            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                Talk to Sales <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
